/* global RequestInit */

import { toast } from 'sonner'

import { GrapeAPIError } from '../../../components/hooks/GrapeAPIError'
import { getEmbeddedOnboardingToken } from '../../../pages/employer_onboarding/embeddedOnboardingStorage'
import { getHeaders } from '../../../utils'

export const ADMIN_SESSION_EXPIRED_MSG = 'Your admin session has expired. Please refresh the page and sign in again to continue.'

export const customFetch = async <T>(
  url: string,
  options: RequestInit
): Promise<T> => {
  // If the body is FormData, don't set Content-Type header - let the browser set it with the correct boundary
  const isFormData = options.body instanceof FormData
  // Include the embedded onboarding client secret for same-origin requests
  const computeIsSameOrigin = (requestUrl: string): boolean => {
    // Be robust in test environments where window.location might be stubbed oddly
    try {
      const requestOrigin = new URL(requestUrl, window.location.origin).origin
      return requestOrigin === window.location.origin
    } catch {
      return false
    }
  }
  const isSameOrigin = computeIsSameOrigin(url)
  const clientSecret = isSameOrigin ? getEmbeddedOnboardingToken() : null
  const authHeader: HeadersInit = clientSecret
    ? { 'Thatch-Onboarding-Session-Client-Secret': clientSecret }
    : {}
  const headers = getHeaders({ ...authHeader, ...options.headers }, { skipContentType: isFormData })
  const requestInit: RequestInit = { ...options, headers }
  const response = await fetch(url, requestInit)

  if (!response.ok) {
    // Handle cases where the response body might be empty or not valid JSON
    let errorJson: { message?: string; errors?: string[]; field_errors?: Record<string, string[]> } = {}
    try {
      const text = await response.text()
      if (text) {
        errorJson = JSON.parse(text)
      }
    } catch {
      // If parsing fails, use empty object as fallback
      errorJson = {}
    }

    if (response.status === 401) {
      try {
        if (window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/')) {
          toast.error(ADMIN_SESSION_EXPIRED_MSG)
        }
      } catch {
        // Ignore cases where window.location isn't available
      }
    }

    // NOTE: This assumes that the error response always has a specific shape.
    // We'll probably need to update this code to account for different error shapes (e.g.
    // based on the status code).
    //
    // Once we start describing errors in the OpenAPI spec, the generated client will be
    // able to handle this and we can probably remove the custom mutator.
    throw GrapeAPIError({
      status: response.status,
      message: errorJson.message ?? '',
      errors: errorJson.errors ?? [],
      fieldErrors: errorJson.field_errors,
      requestId: response.headers.get('x-request-id') ?? undefined,
    })
  }

  if (response.status === 204) {
    return {} as T
  }

  return await response.json()
}
